// Pure decision for the root auth gate in App.tsx. Maps the root `/api/me`
// query state to a screen, with one load-bearing precedence rule:
//
//   pending → real-auth-boundary (401) → last-known-good identity →
//   transient error (no identity) → empty.
//
// WHY the order matters: react-query (v5) retains cached `data` across a FAILED
// BACKGROUND refetch (window-focus / reconnect / staleness) while also setting
// `error`, with isPending=false. So a transient non-401 blip arrives as
// {error:<5xx|network>, hasMe:true}. We must let a VALID cached identity win
// over that transient error — otherwise a momentary blip walls a logged-in user
// behind ReconnectingScreen ("Can't reach the server") despite a live session
// (confirmed prod incident 2026-06-09: retry returned 200 with no re-auth).
//
// A genuine 401 STILL overrides cached identity (real logout). With no cached
// identity, a non-401 error is a genuine can't-reach-on-boot → reconnecting.
// This is a deliberate fail-open on identity: safe because role-gated routes in
// App.tsx fail closed (Navigate away on mismatch) and `me` is identity/role,
// not security-sensitive freshness.
//
// Extracted as a pure function so the branch logic is unit-tested in the node
// test env without rendering React. App.tsx only maps the result to a screen.

import { is401Error } from '../api/client.js';

export type AuthGate = 'loading' | 'login' | 'reconnecting' | 'ready';

export function authGateState(input: {
  isPending: boolean;
  error: unknown;
  hasMe: boolean;
}): AuthGate {
  // In flight (incl. the retry window, when there's no cached data yet):
  // react-query reports `pending` — show loading, not reconnecting.
  if (input.isPending) return 'loading';
  // Real auth boundary: a 401 means the session is genuinely gone — route to
  // Login even if stale identity is still cached.
  if (input.error && is401Error(input.error)) return 'login';
  // Valid cached identity wins over a transient (non-401) error: a failed
  // background refetch must NOT wall a logged-in user. See header.
  if (input.hasMe) return 'ready';
  // No cached identity + a non-401 error → genuine unreachable-on-boot.
  if (input.error) return 'reconnecting';
  // Defensive: settled, no error, no data — treat as logged out.
  return 'login';
}
